import { Injectable } from '@angular/core';
import { GlobalsService } from './globals.service';
import { Passenger, Reservation } from '@app/models';
import { PassportDocumentInt, PassportScanInfoInt } from '@app/interfaces';
import { LocalDataService } from './local-data.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { PAX_TYPE } from '@app/enums';
import { ApiService } from './api.service';
import { getSeatRouteByPassengerCount } from '@app/utils';

@Injectable({
  providedIn: 'root',
})
export class PassengerService {
  passengersSelections: Passenger[];

  constructor(
    private _globalService: GlobalsService,
    private _localeServ: TranslateService,
    private _localDataServ: LocalDataService,
    private _router: Router,
    private _apiService: ApiService
  ) {}

  updateSelectionsPassenger(lookUp: Reservation[]) {
    const reservationSelection = this._globalService.getReservation();
    const reservarionUpdate = lookUp.find(
      (reservation) =>
        reservation.flightSummary.arrivalAirport ==
          reservationSelection.flightSummary.arrivalAirport &&
        reservation.flightSummary.departureAirport ==
          reservationSelection.flightSummary.departureAirport
    );
    this._globalService.setPNRLookUp(lookUp);
    this._globalService.setReservation(reservarionUpdate);
    this.setPassengerSelection(this.passengersSelections);
  }

  getPositionCurrentPassenger() {
    const positionPax = sessionStorage.getItem('positionCurrentPassenger');
    return positionPax ? JSON.parse(positionPax) : undefined;
  }

  setPositionCurrentPassenger(position) {
    sessionStorage.setItem('positionCurrentPassenger', JSON.stringify(position));
  }

  getCurrentPassenger() {
    const position = this.getPositionCurrentPassenger();
    const passagers = this.getPassengerSelection();
    return passagers[position];
  }

  setPassengerSelection(passengers: Array<Passenger>): void {
    const allPassengers = this._globalService.getPassengers();
    this.passengersSelections = [];
    for (let passenger of allPassengers) {
      passenger.checkinSelected = false;
      if (passengers?.find((x) => x.id == passenger.id)) {
        passenger.checkinSelected = true;
        this.passengersSelections.push(passenger);
      }
      this._globalService.setPassenger(passenger);
    }
  }

  getPassengerSelection(includeInfant = false): Array<Passenger> {
    const allPassengers = this._globalService.getPassengers();
    return includeInfant
      ? allPassengers.filter((x) => x.checkinSelected)
      : allPassengers.filter((x) => x.checkinSelected && x.paxType != PAX_TYPE.INFANT);
  }

  getPassengerSelectionbycarryon(includeInfant = false): Array<Passenger> {
    const allPassengers = this._globalService.getPassengers();
    return includeInfant
      ? allPassengers.filter((x) => x.checkinSelected)
      : allPassengers.filter(
          (x) => x.checkinSelected && x.paxType != PAX_TYPE.INFANT && !x.bagCarryon.used
        );
  }

  get hasPassengerToCheckIn() {
    const passengersSelect = this.getPassengerSelection().find(
      (x) => x.checkinSelected == true && !x.checkinStatus
    );
    return passengersSelect ? true : false;
  }

  get findPaxSkyPriority() {
    return !!this._globalService.getPassengers().find((pax) => pax.isSkyPriority);
  }

  getInfantByAdult(adult: Passenger): Passenger | undefined {
    const allPassengers = this._globalService.getPassengers();
    return allPassengers.find(
      (passenger: any) =>
        passenger.paxType === PAX_TYPE.INFANT && passenger.adultAssociate.id === adult.id
    );
  }
  //#region International

  getCurrentPassengerIntl() {
    const position = this.getPositionCurrentPassenger();
    const passagers = this.getPassengerSelection(true);
    return passagers[position];
  }

  getSeatRoute(passengers?: Passenger[]): string {
    return getSeatRouteByPassengerCount();
  }

  nextPageIntl() {
    const passenger = this.getCurrentPassengerIntl();
    if (passenger == undefined) {
      this._router.navigate([this.getSeatRoute()]);
    }
    const nextPage = passenger?.internationFlow.nextPage();
    if (nextPage) {
      this._router.navigate([nextPage]);
    } else {
      //Envio de datos del pasajero que va terminando el flujo
      this.updateCartPaxApi(passenger);
      const passengers = this._globalService.getPassengers();
      const positionCurrentPassenger = this.getPositionCurrentPassenger();
      if (positionCurrentPassenger == passengers.length - 1) {
        this._router.navigate([this.getSeatRoute()]);
      } else {
        const passengerWhitCheckin = passenger?.checkinStatus;
        const nextPosition = positionCurrentPassenger + 1;
        this.setPositionCurrentPassenger(nextPosition);
        const nextPassenger = this.getCurrentPassengerIntl();
        if (nextPassenger && !nextPassenger?.travelDocument?.passportScanned) {
          this._router.navigate(['/pax/scanPassport']);
        } else if (passengerWhitCheckin) {
          this._router.navigate(['/rbmenuPassengerSelection']);
        } else {
          this._router.navigate([this.getSeatRoute()]);
        }
      }
    }
  }

  mapPassportInfoToPassportDocument(passportInfo: PassportScanInfoInt): PassportDocumentInt {
    const country = this._localDataServ.countries.find(
      (x) => x.country3 == passportInfo.issuingCountry
    );
    const nationality = this._localDataServ.countries.find(
      (x) => x.country3 == passportInfo.country
    );
    const currentLang = this._localeServ.currentLang.replace('-', '_');
    let passportDocument: PassportDocumentInt = {
      documentNumber: passportInfo.number,
      issuingCountry: country[currentLang],
      issuingCountryCode: country.country,
      nationality: nationality[currentLang],
      nationalityCode: nationality.country,
      expDay: passportInfo.expirationDateDay,
      expMonth: passportInfo.expirationDateMonth,
      expYear: passportInfo.expirationDateYear,
      selectedGender: passportInfo.gender,
      passportScanned: true,
      numberKTN: '',
      birthDay: passportInfo.birthDateDay,
      birthMonth: passportInfo.birthDateMonth,
      birthYear: passportInfo.birthDateYear,
      birthDate: `${passportInfo.birthDateYear}-${passportInfo?.birthDateMonth?.padStart(
        2,
        '0'
      )}-${passportInfo?.birthDateDay?.padStart(2, '0')}`,
      expirationDate: `${passportInfo?.expirationDateDay?.padStart(
        2,
        '0'
      )}/${passportInfo?.expirationDateMonth?.padStart(2, '0')}/${passportInfo.expirationDateYear}`,
    };
    return passportDocument;
  }

  updateCartPaxApi(passenger) {
    const reservation = this._globalService.getReservation();
    this._apiService.updateCartPax(passenger, reservation).subscribe({
      next: (response) => {},
      error: (error) => {},
      complete: () => {},
    });
  }

  //#endregion International
}
